/**
 * ContactPicker — combobox searchable de contactos (personas).
 *
 * v1.4.1 lo introdujo con fetch propio a /contacts. Ahora consume la FUENTE
 * ÚNICA (`useContacts`, provider en App.tsx): misma cache que el resto de los
 * selectores, así un contacto creado en CUALQUIER módulo aparece aquí sin
 * refetch. El filtrado es client-side (la lista ya está cargada; FLUIDSOL ~40,
 * cap del hook 2000). Construido a mano (sin react-select).
 *
 * Props (sin cambios respecto a v1.4.1, para no tocar BoutiqueViews):
 *   - value: UUID del contacto seleccionado (o null)
 *   - onChange: (uuid: string | null) => void
 *   - placeholder?: string
 *   - onRequestCreate?: () => void  → muestra "+ Nuevo contacto" al final
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useContacts, type Contact } from '../contacts/useContacts';

function label(c: Contact | null | undefined): string {
  if (!c) return '';
  const sub = c.email || c.phone;
  return sub ? `${c.name} · ${sub}` : c.name;
}

export function ContactPicker({
  value,
  onChange,
  placeholder = 'Buscar contacto…',
  onRequestCreate,
}: {
  value: string | null;
  onChange: (uuid: string | null) => void;
  placeholder?: string;
  onRequestCreate?: () => void;
}) {
  const { contacts, loading } = useContacts();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => contacts.find((c) => c.id === value) ?? null,
    [contacts, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? contacts.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.email.toLowerCase().includes(q) ||
            c.phone.toLowerCase().includes(q),
        )
      : contacts;
    return base.slice(0, 50); // tope visual; el usuario refina con la búsqueda
  }, [contacts, query]);

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (c: Contact) => {
    onChange(c.id);
    setOpen(false);
    setQuery('');
  };
  const clear = () => { onChange(null); setQuery(''); };

  return (
    <div className="relative" ref={boxRef}>
      {selected && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="input flex items-center justify-between text-left"
        >
          <span className="truncate text-slate-800">{label(selected)}</span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="ml-2 shrink-0 text-slate-400 hover:text-slate-700"
          >✕</span>
        </button>
      ) : (
        <input
          className="input"
          placeholder={placeholder}
          value={query}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        />
      )}

      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {loading && <p className="px-3 py-2 text-xs text-slate-400">Cargando contactos…</p>}
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">Sin resultados.</p>
          )}
          {!loading && filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              className="block w-full truncate px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <span className="font-medium text-slate-800">{c.name}</span>
              {(c.email || c.phone) && (
                <span className="text-slate-400"> · {c.email || c.phone}</span>
              )}
            </button>
          ))}
          {onRequestCreate && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onRequestCreate(); }}
              className="mt-1 block w-full border-t border-slate-100 px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-slate-50"
            >+ Nuevo contacto</button>
          )}
        </div>
      )}
    </div>
  );
}
